import { useMemo } from 'react';
import type { GameSpec } from '../../../games/spec/schema.ts';
import type { Seat } from '../../../shared/domain.ts';
import { Board } from '../match/Board.tsx';
import type { CellNote } from '../match/Board.tsx';
import { Disc } from '../match/Disc.tsx';
import { markerOf, themeVariables } from '../match/presentation.ts';
import { POSITIONS_PER_RUN, otherSeat, useParity } from './useParity.ts';
import type { ParityView } from './useParity.ts';

export interface ParityPageProps {
  readonly spec: GameSpec;
  readonly onExit: () => void;
  /** Runs the engine. Defaults to a real worker; tests pass their own. */
  readonly createWorker?: () => Worker;
}

/** Columns and rows are 1-based on screen; the engine counts from zero. */
function human(index: number): string {
  return String(index + 1);
}

/** How to name a seat to the person doing the counting. */
function whose(view: ParityView, seat: Seat | null): string {
  if (seat === null) return 'nobody';
  return seat === view.yourSeat ? 'yours' : 'theirs';
}

function whoTakes(view: ParityView, seat: Seat | null): string {
  if (seat === null) return 'nobody';
  return seat === view.yourSeat ? 'you' : 'them';
}

/**
 * The fill order read out: "you, them, you, them".
 *
 * The count is the rule; this is the rule done out loud, and it is the part a
 * player can repeat to themselves over a real board.
 */
function ladderWords(view: ParityView): string {
  return view.ladder.map((step) => whoTakes(view, step.seat as Seat)).join(', ');
}

/**
 * What the screen says about where you are.
 *
 * Facts, not praise, like the rest of the teaching surfaces here: the reason is
 * the part that transfers to the next game.
 */
function statusLine(view: ParityView): string {
  switch (view.phase) {
    case 'seeking':
      return 'Finding a position…';
    case 'counting':
      return view.countMisses > 0
        ? 'Not that many. Count only the empty squares in that column, from the top of the discs up to the ring.'
        : 'How many empty squares are under the ringed square?';
    case 'owning':
      return view.ownerMisses > 0
        ? 'Not that one. An even count falls to the player to move; an odd count falls to the other player.'
        : `You counted ${String(view.counted ?? 0)}. ${
            (view.counted ?? 0) % 2 === 0 ? 'Even' : 'Odd'
          } — so who takes that square?`;
    case 'proving':
      return view.gaps === null
        ? 'That square is taken.'
        : `${String(view.gaps)} empty below, so it is disc ${String(
            view.gaps + 1,
          )} into column ${human(view.targetCol ?? 0)}. Count them off: ${ladderWords(
            view,
          )} — the square is ${whose(view, view.owner)}.`;
    case 'playing':
      return tempoLine(view);
    case 'finished':
      return view.correct === view.total
        ? `All ${String(view.total)} counted right first time. Now do it on a live board.`
        : `${String(view.correct)} of ${String(view.total)} right first time.`;
    case 'stuck':
      return 'No position could be found right now.';
    default:
      return '';
  }
}

/**
 * The sentence the whole third step exists for.
 *
 * Every claim here is read off the board after the move, not predicted before
 * it: whether the square changed hands is a comparison of two counts, and the
 * reason is which column the disc went into.
 */
function tempoLine(view: ParityView): string {
  if (view.takenBy !== null) {
    return `That square is now ${whose(view, view.takenBy)} disc. Undo to fill it in a different order.`;
  }
  if (view.lastColumn === null || view.owner === null) return '';

  const inColumn = view.lastColumn === view.targetCol;
  const changed = view.owner !== view.openingOwner;

  if (inColumn) {
    return `That disc went into the same column, so the count came down with it — the square is still ${whose(
      view,
      view.owner,
    )}. Filling under a square takes the slot you were owed; it does not change who gets the top.`;
  }
  return changed
    ? `That move was somewhere else. The turn passed, and the square changed hands — it is ${whose(
        view,
        view.owner,
      )} now. Every move played outside a column flips who gets the squares in it.`
    : `Somewhere else again, so it has flipped back: the square is ${whose(
        view,
        view.owner,
      )}. Two moves elsewhere leave the count where it started, which is why the fight is over who runs out of safe moves first.`;
}

/**
 * The odd/even count, taught rather than tested.
 *
 * The curriculum's parity lesson states the rule and then asks you to point at
 * a column, which is a test with the working left out. Here the working is the
 * screen: you count the square yourself, you say whose it is, the column shows
 * its fill order back to you, and then you play a disc and watch the answer
 * move. Nothing is stored — both answers are judged by counting the board that
 * is on screen, through the same functions that drew it.
 */
export function ParityPage({ spec, onExit, createWorker }: ParityPageProps) {
  const { view, begin, count, own, explore, undo, restore, toggleMap, next } = useParity({
    spec,
    ...(createWorker ? { createWorker } : {}),
  });

  const them = otherSeat(spec, view.yourSeat);
  const waiting = view.phase === 'seeking';
  const proven = view.phase === 'proving' || view.phase === 'playing';
  const ending = view.phase === 'finished' || view.phase === 'stuck';
  /** Positions whose questions are behind you, which is what the tally counts. */
  const answered = view.positionNumber - (proven || ending ? 0 : 1);

  /*
   * The numbers, drawn on the squares they describe.
   *
   * Three layers, most specific last: the whole-board map when it is switched
   * on, then the target column's fill order once the answer is proven, then the
   * ring on the square under discussion.
   */
  const notes = useMemo(() => {
    const map = new Map<number, CellNote>();

    for (const square of view.map) {
      map.set(square.cell, {
        text: String(square.gaps),
        seat: square.owner as Seat,
        label: `${String(square.gaps)} empty below, ${whose(view, square.owner as Seat)}`,
      });
    }

    /*
     * The ladder and the map are never on the board together.
     *
     * They are both numbers in circles and they count different things — the
     * ladder counts discs into a column, the map counts empty squares under
     * one. Side by side they produced two "3"s in adjacent squares meaning two
     * different things, which is worse than either on its own.
     */
    if (!view.showMap) {
      for (const step of view.ladder) {
        map.set(step.cell, {
          text: String(step.ordinal),
          seat: step.seat as Seat,
          label: `disc ${String(step.ordinal)} into this column, ${whoTakes(view, step.seat as Seat)}`,
        });
      }
    }

    if (view.target !== null) {
      const existing = map.get(view.target);
      map.set(view.target, {
        text: existing?.text ?? '?',
        seat: proven ? (view.owner ?? existing?.seat ?? null) : null,
        label: existing?.label ?? 'the square being counted',
        focus: true,
      });
    }

    return map;
  }, [view, proven]);

  if (view.phase === 'reading') {
    return (
      <section className="parity" aria-labelledby="parity-heading" style={themeVariables(spec)}>
        <header className="parity__head">
          <h2 id="parity-heading" className="parity__heading">
            The odd/even count
          </h2>
          <p className="parity__intro">
            Who gets a square nobody can reach yet is arithmetic, not luck. This is the whole method,
            and then {POSITIONS_PER_RUN} positions where you do it.
          </p>
        </header>

        <ol className="parity__method">
          <li>
            <h3>Count the empty squares under it</h3>
            <p>
              Pick a square that would finish somebody&rsquo;s line and count the empty squares
              beneath it in its column. <strong>Even</strong> means it falls to whoever moves next.
              <strong> Odd</strong> means it falls to the other player. That is the entire rule: the
              square is disc number <em>count + 1</em> into that column, and the players take turns.
            </p>
          </li>
          <li>
            <h3>The shorthand, and where it stops working</h3>
            <p>
              You will see this taught as <em>odd rows belong to player one, even rows to player
              two</em>. That is this same count with an untouched column and player one to move &mdash;
              a special case. Checked against this project&rsquo;s own exact solver it held in{' '}
              <strong>83.6%</strong> of the positions tested, and it only becomes dependable as the
              board fills: 81% with twenty squares left, 100% with ten. Count the column in front of
              you rather than reading the row number off the board.
            </p>
          </li>
          <li>
            <h3>Every move elsewhere flips it</h3>
            <p>
              Adding a disc under a square keeps the count where it was &mdash; you took the slot you
              were owed. A move in any <em>other</em> column passes the turn, and every square nobody
              has reached changes hands. That is why parity is something you keep counting, and why
              these endgames come down to who runs out of safe moves first.
            </p>
          </li>
        </ol>

        <div className="parity__actions">
          <button type="button" className="button button--primary" onClick={begin}>
            Show me a position
          </button>
          <button type="button" className="button button--quiet" onClick={onExit}>
            Done
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="parity" aria-labelledby="parity-heading" style={themeVariables(spec)}>
      <header className="parity__head">
        <h2 id="parity-heading" className="parity__heading">
          The odd/even count
        </h2>
        {view.positionNumber > 0 ? (
          <p className="parity__progress">
            Position {String(Math.min(view.positionNumber, view.total))} of {String(view.total)}
          </p>
        ) : null}
      </header>

      <div className="parity__layout">
        <div className="parity__stage">
          <Board
            spec={spec}
            board={view.board}
            yourSeat={view.yourSeat}
            legalColumns={view.legalColumns}
            pendingColumn={null}
            lastMoveIndex={null}
            winningLine={null}
            interactive={proven && view.legalColumns.length > 0}
            onDrop={explore}
            columnControls={proven}
            notes={notes}
          />
        </div>

        <div className="parity__side">
          {/*
            One live region, as everywhere else here. The board already
            describes its own squares; a second channel narrating the same
            numbers would talk over the sentence that says what to do.
          */}
          <p className="parity__status" role="status">
            {waiting ? <span className="spinner" aria-hidden="true" /> : null}
            {statusLine(view)}
          </p>

          {view.error !== null ? <p className="parity__error">{view.error}</p> : null}

          {view.phase === 'counting' ? (
            <div className="parity__answers" role="group" aria-label="How many empty squares">
              {Array.from({ length: view.maxGaps + 1 }, (_, value) => (
                <button
                  key={value}
                  type="button"
                  className="parity__number"
                  onClick={() => count(value)}
                >
                  {value}
                </button>
              ))}
            </div>
          ) : null}

          {/*
            The two answers wear the discs they are about. "You" and "Them" as
            bare words would be two identical buttons for a question whose whole
            subject is which colour ends up on that square — and the disc brings
            the marker shape with it, so the pair is still telling them apart
            with the colour removed.
          */}
          {view.phase === 'owning' ? (
            <div className="parity__answers" role="group" aria-label="Who takes that square">
              <button
                type="button"
                className="parity__owner"
                onClick={() => view.yourSeat !== null && own(view.yourSeat)}
              >
                <span className="parity__ownerDisc">
                  {view.yourSeat === null ? null : (
                    <Disc cell={view.yourSeat} marker={markerOf(spec, view.yourSeat)} />
                  )}
                </span>
                You
              </button>
              <button
                type="button"
                className="parity__owner"
                onClick={() => them !== null && own(them)}
              >
                <span className="parity__ownerDisc">
                  {them === null ? null : <Disc cell={them} marker={markerOf(spec, them)} />}
                </span>
                Them
              </button>
            </div>
          ) : null}

          {/*
            The shorthand held up against the count, on this square.
            Where they disagree is the most useful thing on the screen, so it
            is stated rather than quietly omitted.
          */}
          {proven && view.rowRule !== null && view.owner !== null && view.explored === 0 ? (
            <p className="parity__note">
              The row-number shorthand calls row {human(view.targetRow ?? 0)}{' '}
              {view.rowRule === view.yourSeat ? 'yours' : 'theirs'}, and the count here says{' '}
              {whose(view, view.owner)} &mdash;{' '}
              {view.rowRule === view.owner ? 'the two agree.' : 'they disagree, and the count is the one that is true.'}
            </p>
          ) : null}

          {proven && view.threatFor.length > 0 && view.owner !== null && view.gaps !== null ? (
            <p className="parity__note">
              {view.threatFor.includes(view.owner)
                ? `That square finishes a line for ${whoTakes(view, view.owner)} — and the count gives it to ${whoTakes(view, view.owner)}.`
                : `That square finishes a line for ${whoTakes(view, view.threatFor[0] as Seat)}, but the count gives it to ${whoTakes(view, view.owner)}.`}
            </p>
          ) : null}

          <div className="parity__actions">
            {proven ? (
              <button type="button" className="button button--primary" onClick={next}>
                {view.positionNumber >= view.total
                  ? 'Finish'
                  : view.nextReady
                    ? 'Next position'
                    : 'Next position (finding…)'}
              </button>
            ) : null}

            {view.explored > 0 ? (
              <button type="button" className="button button--quiet" onClick={undo}>
                Undo
              </button>
            ) : null}

            {view.explored > 1 ? (
              <button type="button" className="button button--quiet" onClick={restore}>
                Back to the position
              </button>
            ) : null}

            {ending ? (
              <button type="button" className="button button--primary" onClick={onExit}>
                Done
              </button>
            ) : null}
          </div>

          {proven ? (
            <p className="parity__try">
              Now play a disc anywhere and watch the count move.
            </p>
          ) : null}

          <button
            type="button"
            className="button button--quiet parity__mapToggle"
            onClick={toggleMap}
            aria-pressed={view.showMap}
          >
            {view.showMap ? 'Hide every square' : 'Count every square'}
          </button>

          {/*
            Counted over positions that have been *answered*, not over the one
            on screen. "0 of 1" while the question is still open reads as a
            verdict on an answer nobody has given yet.
          */}
          <p className="parity__tally">
            {answered === 0
              ? ''
              : `${String(view.correct)} of ${String(answered)} right first time.`}
          </p>

          {ending ? null : (
            <footer className="parity__exit">
              <button type="button" className="button button--quiet" onClick={onExit}>
                Done
              </button>
            </footer>
          )}
        </div>
      </div>
    </section>
  );
}
